/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import MinusIcon from '@material-ui/icons/Remove'
import React from 'react'

import { getTranslate } from '../../localization'
import useStyles from './styles/index.styles'

type Props = {
  value: number
  setValue: (val: number) => void
  disable: boolean
  max?: number
  min?: number
  smallSize?: boolean
  loading?: boolean
  /**
   * The increase step is a no-op — typically because `max` is 0.
   *
   * Deliberately NOT folded into `disable`: that single flag feeds BOTH
   * buttons, so reusing it here would also disable decrease and trap a user who
   * has already added rows and wants to remove them — a worse bug than the one
   * this reports. It is also already driven by the loading and payment-method
   * paths at the call sites.
   *
   * Renders as `aria-disabled`, not `disabled`, so the control keeps its place
   * in the tab order: a disabled button is skipped entirely, and the user never
   * reaches the explanation of why it cannot be used.
   */
  increaseBlocked?: boolean
  /** id of the element explaining why increase is blocked. */
  increaseDescribedBy?: string
  increaseLabel?: string
  decreaseLabel?: string
}

const CustomCounter = ({
  value,
  setValue,
  disable,
  max,
  min,
  smallSize,
  loading,
  increaseBlocked,
  increaseDescribedBy,
  increaseLabel,
  decreaseLabel
}: Props) => {
  const classes = useStyles()
  const increase = () => {
    if (max !== undefined) {
      if (value < max && max !== 0) {
        setValue(value + 1)
      }
    } else {
      setValue(value + 1)
    }
  }

  const decrease = () => {
    if (value != 0 && (min === undefined || value > min)) {
      setValue(value - 1)
    }
  }
  return (
    <div className={[classes.root, smallSize && classes.rootSmall].join(' ')}>
      <IconButton
        onClick={() => {
          decrease()
        }}
        className={[classes.button, smallSize && classes.buttonSmall].join(' ')}
        size="small"
        aria-label={decreaseLabel || getTranslate('Decrease')}
        disabled={disable}>
        <MinusIcon />
      </IconButton>

      <Typography
        className={[
          classes.countValue,
          smallSize && classes.countValueSmall
        ].join('')}
        color="primary">
        {loading ? (
          <CircularProgress className={classes.circularProgress} size={12} />
        ) : (
          value
        )}
      </Typography>

      <IconButton
        onClick={() => {
          increase()
        }}
        className={[
          classes.button,
          smallSize && classes.buttonSmall,
          increaseBlocked && classes.buttonBlocked
        ].join(' ')}
        size="small"
        aria-label={increaseLabel || getTranslate('Increase')}
        aria-disabled={increaseBlocked || undefined}
        aria-describedby={increaseBlocked ? increaseDescribedBy : undefined}
        disabled={disable}>
        <AddIcon />
      </IconButton>
    </div>
  )
}

export default CustomCounter
